import React, { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { getAppointment, deleteAppointment } from '../services/appointments'
import { getServiceById } from '../services/services'
import { getBusinessById } from '../services/business'
import './AppointmentConfirmation.css'

function AppointmentConfirmation() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const appointmentId = searchParams.get('appointmentId');
    const businessId = searchParams.get('businessId');
    const serviceId = searchParams.get('serviceId');
    const [appointmentData, setAppointmentData] = useState(null);
    const [serviceData, setServiceData] = useState(null);
    const [businessData, setBusinessData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isCancelled, setIsCancelled] = useState(false);
    const [cancellationResult, setCancellationResult] = useState(null);

    useEffect(() => {
        const fetchAppointmentDetails = async () => {
            // console.log(appointmentId, businessId, serviceId);
            try {
                setLoading(true);
                setError(null);
                
                if (!appointmentId || !businessId || !serviceId) {
                    throw new Error('Missing required parameters');
                }

                // Fetch appointment data
                const appointment = await getAppointment(appointmentId, businessId, serviceId);
                setAppointmentData(appointment);
                setServiceData(appointment.service)
                setBusinessData(appointment.business)
            } catch (error) {
                console.error("Error fetching appointment details:", error);
                setError('Failed to load appointment details. Please try again.');
            } finally {
                setLoading(false);
            }
        }

        fetchAppointmentDetails();
    }, [appointmentId, businessId, serviceId]);

    const formatDateTime = (isoString) => {
        const date = new Date(isoString);
        const dateOptions = { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        };
        const timeOptions = { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true
        };
        
        return {
            date: date.toLocaleDateString('en-US', dateOptions),
            time: date.toLocaleTimeString('en-US', timeOptions)
        };
    };

    const handleEditAppointment = () => {
        // Navigate back to booking page with current appointment data
        navigate(`/booking?businessId=${businessId}&selectedServiceId=${serviceData?.id}&edit=true&appointmentId=${appointmentId}`);
    };

    const handleDeleteAppointment = async () => {
        const confirmMessage = `Please note: Cancellations made less than 24 hours before your appointment are not eligible for refund.\nAre you sure you want to cancel your appointment on ${formatDateTime(appointmentData.start).date} at ${formatDateTime(appointmentData.start).time}?`;
        
        if (window.confirm(confirmMessage)) {
            try {
                setLoading(true);
                const result = await deleteAppointment(businessId, serviceId, appointmentId);
                
                // console.log('Delete appointment result:', result);
                
                // Handle the response from the backend
                if (result.success) {
                    setCancellationResult(result);
                    setIsCancelled(true);
                } else {
                    // Handle specific error messages from backend
                    let errorMessage = result.message || 'Failed to cancel appointment. Please try again or contact us directly.';
                    
                    // Handle refund failure case
                    if (result.refund_error) {
                        errorMessage += `\n\nRefund Error: ${result.refund_error}`;
                    }
                    
                    alert(errorMessage);
                }
                setLoading(false);
            } catch (error) {
                console.error('Error cancelling appointment:', error);
                
                // Check if it's a specific error response from the backend
                if (error.response && error.response.data && error.response.data.message) {
                    alert(error.response.data.message);
                } else {
                    alert('Failed to cancel appointment. Please try again or contact us directly.');
                }
                setLoading(false);
            }
        }
    };

    const handleBackToHome = () => {
        navigate('/');
    };

    if (loading) {
        return (
            <div className="confirmation-container">
                <div className="confirmation-card loading">
                    <div className="loading-spinner"></div>
                    <p>Loading appointment details...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="confirmation-container">
                <div className="confirmation-card error">
                    <div className="error-icon">⚠️</div>
                    <h2>Unable to Load Appointment</h2>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    if (!appointmentData || !serviceData || !businessData) {
        return (
            <div className="confirmation-container">
                <div className="confirmation-card error">
                    <div className="error-icon">❌</div>
                    <h2>Appointment Not Found</h2>
                    <p>The requested appointment could not be found.</p>
                    <button className="btn-primary" onClick={handleBackToHome}>
                        Return to Home
                    </button>
                </div>
            </div>
        );
    }

    const { date, time } = formatDateTime(appointmentData.start);

    // Show cancellation confirmation if appointment was cancelled
    if (isCancelled && cancellationResult) {
        const { _, message, confirmation } = cancellationResult;
        const cancelledDate = confirmation?.deleted_at ? new Date(confirmation.deleted_at) : new Date();
        
        // // Debug logging
        // console.log('Cancellation result:', cancellationResult);
        // console.log('Confirmation object:', confirmation);
        // console.log('Refund object:', confirmation?.refund);
        
        return (
            <div className="confirmation-container">
                <div className="confirmation-card cancelled">
                    <div className="confirmation-header cancelled">
                        <div className="cancelled-icon">🗑️</div>
                        <h1>Appointment Cancelled</h1>
                        <p className="confirmation-subtitle">
                            {message || 'Your appointment has been successfully cancelled'}
                        </p>
                    </div>

                    <div className="appointment-details">
                        <table className="details-table">
                            <tbody>
                                <tr>
                                    <td className="label">Customer Name:</td>
                                    <td className="value">{appointmentData.client_data.name}</td>
                                </tr>
                                <tr>
                                    <td className="label">Cancelled Appointment:</td>
                                    <td className="value">
                                        <div>{date}</div>
                                        <div className="time-small">{time}</div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="label">Cancelled On:</td>
                                    <td className="value">
                                        <div>{cancelledDate.toLocaleDateString('en-US', { 
                                            weekday: 'long', 
                                            year: 'numeric', 
                                            month: 'long', 
                                            day: 'numeric' 
                                        })}</div>
                                        <div className="time-small">{cancelledDate.toLocaleTimeString('en-US', { 
                                            hour: 'numeric', 
                                            minute: '2-digit',
                                            hour12: true
                                        })}</div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="label">Phone:</td>
                                    <td className="value">{appointmentData.client_data.contact_number}</td>
                                </tr>
                                <tr>
                                    <td className="label">Email:</td>
                                    <td className="value">{appointmentData.client_data.email}</td>
                                </tr>
                                <tr>
                                    <td className="label">Service:</td>
                                    <td className="value">{serviceData.service || serviceData.service_name}</td>
                                </tr>
                                <tr>
                                    <td className="label">Duration:</td>
                                    <td className="value">{serviceData.duration} minutes</td>
                                </tr>
                                <tr>
                                    <td className="label">Total:</td>
                                    <td className="value">£{serviceData.price}</td>
                                </tr>
                                {confirmation?.refund && (
                                    <>
                                        <tr>
                                            <td className="label">Refund Status:</td>
                                            <td className="value refund-status">
                                                {confirmation.refund.success ? 
                                                    <span className="refund-success">✅ Refund Processed</span> : 
                                                    <span className="refund-failed">❌ Refund Failed</span>
                                                }
                                            </td>
                                        </tr>
                                        {confirmation.refund.success && (
                                            <>
                                                <tr>
                                                    <td className="label">Refund Amount:</td>
                                                    <td className="value refund-amount">£{confirmation.refund.amount_gbp || (confirmation.refund.amount / 100).toFixed(2)}</td>
                                                </tr>
                                            </>
                                        )}
                                        {!confirmation.refund.success && confirmation.refund.error && (
                                            <tr>
                                                <td className="label">Refund Error:</td>
                                                <td className="value refund-error">{confirmation.refund.error}</td>
                                            </tr>
                                        )}
                                    </>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="confirmation-footer">
                        <p className="footer-text">
                            {confirmation?.refund?.success ? 
                                'Your appointment has been cancelled and refund has been processed. The refund should appear in your account within 3-5 business days.' :
                                confirmation?.refund ? 
                                'Your appointment has been cancelled. There was an issue processing the refund. Please contact us for assistance.' :
                                'Your appointment has been cancelled. If you need to reschedule, please book a new appointment.'
                            }
                        </p>
                        {confirmation?.refund?.success && (
                            <p className="footer-text refund-note">
                                <strong>Refund Reference:</strong> {confirmation.refund.refund_id}
                                {confirmation.refund.receipt_number && (
                                    <>
                                        <br />
                                        <strong>Receipt Number:</strong> {confirmation.refund.receipt_number}
                                    </>
                                )}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="confirmation-container">
            <div className="confirmation-card">
                <div className="confirmation-header">
                    <div className="success-icon">✅</div>
                    <h1>Appointment Confirmed!</h1>
                    <p className="confirmation-subtitle">
                        Your appointment has been successfully booked
                    </p>
                </div>

                <div className="appointment-details">
                    <table className="details-table">
                        <tbody>
                            <tr>
                                <td className="label">Customer Name:</td>
                                <td className="value">{appointmentData.client_data.name}</td>
                            </tr>
                            <tr>
                                <td className="label">Date & Time:</td>
                                <td className="value">
                                    <div>{date}</div>
                                    <div className="time-small">{time}</div>
                                </td>
                            </tr>
                            <tr>
                                <td className="label">Phone:</td>
                                <td className="value">{appointmentData.client_data.contact_number}</td>
                            </tr>
                            <tr>
                                <td className="label">Email:</td>
                                <td className="value">{appointmentData.client_data.email}</td>
                            </tr>
                            <tr>
                                <td className="label">Service:</td>
                                <td className="value">{serviceData.service || serviceData.service_name}</td>
                            </tr>
                            <tr>
                                <td className="label">Duration:</td>
                                <td className="value">{serviceData.duration} minutes</td>
                            </tr>
                            <tr>
                                <td className="label">Total:</td>
                                <td className="value price">£{(serviceData.price).toFixed(2)}</td>
                            </tr>
                            <tr>
                                <td className="label">Paid amount:</td>
                                <td className="value">£{(appointmentData.payment_amount).toFixed(2)}</td>
                            </tr>
                            <tr>
                                <td className="label">Remaining to pay:</td>
                                <td className="value">£{(serviceData.price - appointmentData.payment_amount).toFixed(2)}</td>
                            </tr>
                            <tr>
                                <td className="label">Business:</td>
                                <td className="value">{businessData.name || businessData.business_name}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="confirmation-actions">
                    <button 
                        className="btn-secondary" 
                        onClick={handleEditAppointment}
                        disabled={false}
                        title={"Modify your appointment"}
                    >
                        ✏️ Edit Appointment
                    </button>
                    <button 
                        className="btn-danger" 
                        onClick={handleDeleteAppointment}
                        disabled={false}
                        title={"Cancel this appointment"}
                    >
                        🗑️ Cancel Appointment
                    </button>
                </div>

                <div className="confirmation-footer">
                    <p className="footer-text">
                        Please arrive 5 minutes before your scheduled appointment time.
                        If you need to make changes or cancel, please do so at least 12 and 24 hours in advance.
                        Please note: Cancellations made less than 24 hours before your appointment are not eligible for refund.
                    </p>
                    <button 
                        className="btn-feedback" 
                        onClick={() => navigate(`/feedback?businessId=${businessId}`)}
                    >
                        💬 Share Your Feedback
                    </button>
                </div>
            </div>
        </div>
    );
}

export default AppointmentConfirmation